import { useState, useEffect, useCallback } from "react";

import {
  ArrowUpDown,
  ChevronDown,
  X,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Skeleton } from "../components/ui/skeleton";
import coin from "../assets/images/placeholder/crypto-colour.png";
import { useToast } from "../hooks/use-toast";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { useCryptoAssets, useFetchPairs } from "../hooks/query/queries";
import { useTradeRate } from "../hooks/useTradeRate";
import { useNotification } from "../context/NotificationProvider";
import { truncateDecimals } from "../utils/numberFormatter";
export default function AssetSelectorForex({
  onSelect,
  onChange,
  setParentRate,
  currencies,
  onTargetCancel,
  pair,
  onTarget,
  onSelectedAsset,
  onFetcedRate,
  onReject,
  onAccept,
  hotActionAsset,
  setSelectedAsset,
  selectedAsset,
  amount,
  setIsBaseInputParent,
  setAmount,
  isPostTrade,
}) {
  const [isBaseInput, setIsBaseInput] = useState(false);
  const [tragetAmount, setTragetAmount] = useState(null);
  const [loadingRate, setLoadingRate] = useState(true);
  const [rate, setRate] = useState(null);
  const [convertedValue, setConvertedValue] = useState(" ");
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [awaitingRate, setAwaitingRate] = useState(false);
  const [flag, setFlag] = useState(false);
  const [minBuy, setMinBuy] = useState(null);
  const [flagMinBuy, setFlagMinBuy] = useState(false);
  const [highTarget, setHighTarget] = useState(false);
  const [targetAmountHolder, setTargetAmountHolder] = useState(null);
  const { toast } = useToast();
  const { showPersistentMessage } = useNotification();
  const { isLoading } = useCryptoAssets();
  const { data: assetPairs } = useFetchPairs(selectedAsset?.assetId);

  const { data: tradeRate } = useTradeRate(
    selectedAsset?.assetId,
    "buy",
    pair?.asset,
    isPostTrade
  );

  useEffect(() => {
    if (!pair || !selectedAsset) return;
    setLoadingRate(true);
    setAwaitingRate(true);
  }, [pair, selectedAsset]);

  useEffect(() => {
    const setTradeRate = () => {
      try {
        if (tradeRate) {
          setRate(tradeRate);
          setParentRate?.(tradeRate);
        }
      } catch (error) {
        if (error) {
          showPersistentMessage("error", `${error}`);
        }
      } finally {
        setLoadingRate(false);
        setAwaitingRate(false);

        if (onFetcedRate) {
          onFetcedRate();
        }
      }
    };
    setTradeRate();
  }, [tradeRate, setParentRate, showPersistentMessage, onFetcedRate]);

  const filteredAssets = currencies?.filter(
    (assets) =>
      assets?.asset?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      assets?.assetName?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const handleSelect = useCallback(
    (asset) => {
      if (asset?.asset === selectedAsset?.asset) {
        setSelectedAsset(asset);
        setOpen(false);
        return;
      }
      if (asset?.asset !== selectedAsset?.asset) {
        setAwaitingRate(true);
        setLoadingRate(true);
        setSelectedAsset(asset);
        if (onSelect) {
          onSelect(asset);
        }
        if (onSelectedAsset) {
          onSelectedAsset();
        }
        setOpen(false);
      }
    },
    [selectedAsset, setSelectedAsset, onSelect, onSelectedAsset]
  );

  useEffect(() => {
    if (!hotActionAsset) return;
    handleSelect(hotActionAsset);
  }, [hotActionAsset, handleSelect]);

  const onTargetChange = (event) => {
    const newTarget = event.target.value;
    if (newTarget > rate?.rate) {
      setTargetAmountHolder(newTarget);
      setTragetAmount(null);
      setHighTarget(true);
      toast({
        description: "Target amount cannot be higher than default rate",
        variant: "eighty",
      });
    }
    if (newTarget <= rate?.rate) {
      setTargetAmountHolder(newTarget);
      setHighTarget(false);
    }
  };

  const handleTargetDone = () => {
    if (targetAmountHolder <= rate?.rate) {
      setTragetAmount(targetAmountHolder);
      setHighTarget(false);
      setOrderOpen(false);
      if (onTarget) {
        onTarget(targetAmountHolder);
      }
    }
  };

  const takeInOutAmount = useCallback(
    (newAmount, convertedValue) => {
      if (onChange) {
        onChange(newAmount, convertedValue);
      }
    },
    [onChange]
  );

  const takeOutInAmount = useCallback(
    (convertedValue, newAmount) => {
      if (onChange) {
        onChange(convertedValue, newAmount);
      }
    },
    [onChange]
  );

  const onTextChange = (event) => {
    setLoadingRate(false);
    const rawValue = event.target.value;

    if (/^\d*\.?\d*$/.test(rawValue)) {
      setAmount(rawValue);
      const newAmount = parseFloat(rawValue);
      if (!isNaN(newAmount) && assetPairs && pair) {
        const matchingPairInfo = assetPairs.find(
          (info) => info.pair === pair.pair
        );

        if (matchingPairInfo && matchingPairInfo.minBuyAmountTail) {
          const minBuyRaw =
            typeof matchingPairInfo.minBuyAmountTail === "number"
              ? matchingPairInfo.minBuyAmountTail
              : parseFloat(
                  matchingPairInfo.minBuyAmountTail.$numberDecimal || "0"
                );

          const minBuy = isBaseInput
            ? minBuyRaw / (rate?.rate ?? 1)
            : minBuyRaw;

          setMinBuy(minBuy);

          if (newAmount >= minBuy && newAmount <= pair?.availableAmount) {
            setAmount(rawValue);
            if (onAccept) {
              onAccept();
            }
            setFlag(false);
            setFlagMinBuy(false);
          } else if (newAmount < minBuy) {
            setFlagMinBuy(true);
            if (onReject) {
              onReject();
            }
          } else if (newAmount > pair?.availableAmount) {
            setFlag(true);
            setFlagMinBuy(false);
            if (onReject) {
              onReject();
            }
          }
        } else {
          setMinBuy(null);
          setFlagMinBuy(false);
          if (newAmount > pair?.availableAmount) {
            setFlag(true);
            if (onReject) {
              onReject();
            }
          } else {
            setFlag(false);
            if (onAccept) {
              onAccept();
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    const number = parseFloat(amount || 0);

    if (isNaN(number) || number === 0) {
      setConvertedValue(0);
      return;
    }

    if (isBaseInput) {
      if (!tragetAmount) {
        const convertedValue = parseFloat(number * rate?.rate);
        const asset = selectedAsset?.asset?.toLowerCase();
        const isTwoDecimalCurrency = asset === "usdc" || asset === "usdt";
        const formattedValue = isTwoDecimalCurrency
          ? parseFloat(convertedValue.toFixed(2))
          : convertedValue;
        setConvertedValue(formattedValue);
        if (convertedValue > pair?.availableAmount) {
          if (!isPostTrade) {
            setFlag(true);
            if (onReject) {
              onReject();
            }
          }
          if (isPostTrade) {
            setFlag(false);
            if (onAccept) {
              onAccept();
            }
            takeInOutAmount(formattedValue, number);
          }
        }
        if (convertedValue <= pair?.availableAmount) {
          setFlag(false);
          if (onAccept) {
            onAccept();
          }
          takeInOutAmount(formattedValue, number);
        }
      }
      if (tragetAmount) {
        const convertedValue = number * tragetAmount;
        const asset = selectedAsset?.asset?.toLowerCase();
        const isTwoDecimalCurrency = asset === "usdc" || asset === "usdt";
        const formattedValue = isTwoDecimalCurrency
          ? parseFloat(convertedValue.toFixed(2))
          : convertedValue;
        setConvertedValue(formattedValue);
        takeInOutAmount(formattedValue, number);
      }
    } else {
      if (!tragetAmount) {
        const convertedValue = parseFloat(number / rate?.rate);
        const formattedValue = parseFloat(convertedValue.toFixed(2));
        setConvertedValue(formattedValue);
        if (number > pair?.availableAmount) {
          if (!isPostTrade) {
            setFlag(true);
            if (onReject) {
              onReject();
            }
          }
          if (isPostTrade) {
            setFlag(false);
            if (onAccept) {
              onAccept();
            }
            takeOutInAmount(number, formattedValue);
          }
        }
        if (number <= pair?.availableAmount) {
          setFlag(false);
          if (onAccept) {
            onAccept();
          }
          takeOutInAmount(number, formattedValue);
        }
      }
      if (tragetAmount) {
        const convertedValue = number / tragetAmount;
        const formattedValue = parseFloat(convertedValue.toFixed(2));
        setConvertedValue(formattedValue);
        takeOutInAmount(number, formattedValue);
      }
    }
  }, [
    amount,
    rate,
    isBaseInput,
    tragetAmount,
    isPostTrade,
    onAccept,
    onReject,
    pair,
    takeInOutAmount,
    takeOutInAmount,
    selectedAsset?.asset,
  ]);

  const maxQuoteAmount = () => {
    if (!pair?.availableAmount || !rate?.rate) {
      return;
    }

    let newAmount;

    if (isBaseInput) {
      newAmount = pair.availableAmount / rate.rate;
      if (isNaN(newAmount) || newAmount <= 0) {
        return;
      }
      setAmount(newAmount.toString());
    } else {
      newAmount = pair.availableAmount;
      if (isNaN(newAmount) || newAmount <= 0) {
        return;
      }
      setAmount(newAmount.toString());
    }
  };

  const switchConversion = () => {
    const currentAmount = parseFloat(amount || 0);
    const currentConverted = parseFloat(convertedValue || 0);
    const newIsBaseInput = !isBaseInput;

    setIsBaseInput(newIsBaseInput);
    setIsBaseInputParent(newIsBaseInput);

    if (newIsBaseInput) {
      setAmount(currentConverted.toString());
      setConvertedValue(currentAmount || 0);
      if (!tragetAmount && rate?.rate) {
        takeInOutAmount(currentAmount || 0, currentConverted);
      } else if (tragetAmount) {
        takeInOutAmount(currentConverted * tragetAmount, currentConverted);
      }
    } else {
      setAmount(currentConverted.toString());
      setConvertedValue(currentAmount || 0);
      if (!tragetAmount && rate?.rate) {
        takeOutInAmount(currentConverted, currentAmount || 0);
      } else if (tragetAmount) {
        takeOutInAmount(currentConverted, currentConverted / tragetAmount);
      }
    }
  };
  const handleCancel = () => {
    setTragetAmount(null);
    setTargetAmountHolder(null);
    setOrderOpen(false);
  };

  return (
    <>
      <div className="grid grid-cols-4 gap-4 grid-rows-1 py-8">
        <div className="space-y-2 col-span-1">
          <label className="text-gray-600 text-sm">Select asset</label>
          <Dialog open={open} onOpenChange={setOpen} modal={false}>
            <DialogTrigger asChild>
              {isLoading ? (
                <div className="w-full flex items-center justify-between py-4  px-4 rounded-2xl border border-gray-300 bg-muted/50">
                  <div className="flex items-center gap-3 ">
                    <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />

                    <div className="flex flex-col gap-1">
                      <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                    </div>
                  </div>
                  <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
                </div>
              ) : (
                <Button
                  disabled={awaitingRate}
                  variant="outline"
                  className="w-full flex items-center justify-between py-8 rounded-2xl hover:border-brand-primary transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center">
                      {selectedAsset ? (
                        <img
                          src={selectedAsset?.assetIcon}
                          alt={selectedAsset?.assetName}
                          width={32}
                          height={32}
                          className="text-white"
                        />
                      ) : (
                        <img
                          src={coin}
                          alt={"Default Asset"}
                          width={32}
                          height={32}
                          className="text-white"
                        />
                      )}
                    </div>
                    <div className="text-left">
                      <div className="font-semibold">
                        {selectedAsset?.asset.toUpperCase()}
                      </div>
                      <div className="text-gray-600">
                        {selectedAsset?.assetName
                          ? selectedAsset.assetName.length > 14
                            ? selectedAsset.assetName.slice(0, 14) + "..."
                            : selectedAsset.assetName
                          : "Select..."}
                      </div>
                    </div>
                  </div>
                  <ChevronDown className="text-gray-400" />
                </Button>
              )}
            </DialogTrigger>

            <DialogContent className="max-w-sm rounded-2xl p-6">
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                >
                  <X size={18} className="text-gray-700" />
                </Button>
              </DialogClose>

              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-gray-800">
                  Select an Asset
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <input
                  type="text"
                  placeholder="Search addresses..."
                  className="w-full border rounded-lg px-3 py-2"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {
                <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
                  {currencies &&
                    filteredAssets.map((asset) => {
                      return (
                        <button
                          key={asset.assetId}
                          onClick={() => {
                            handleSelect(asset);
                          }}
                          className="flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200"
                        >
                          <img
                            src={asset.assetIcon}
                            alt={asset.assetName}
                            className="w-6 h-6"
                          />
                          <div className="flex flex-col text-left">
                            <span>{asset.assetName}</span>
                            <span className="text-xs text-gray-500">
                              {asset.asset?.toUpperCase()}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                </div>
              }
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-2 col-span-3">
          <label className="text-gray-600 text-sm">{"Enter Amount"}</label>
          <div
            className={`flex items-center gap-4 rounded-2xl p-3 border ${
              flag || flagMinBuy ? "border-2 border-rose-500 " : " "
            }`}
          >
            <input
              type="text"
              value={amount}
              onChange={onTextChange}
              className={`flex-1 text-base md:text-sm font-semibold bg-transparent outline-none 
    focus-visible:ring-0 focus-visible:outline-none transition-colors duration-200 ease-in-out
    ${flag ? "text-rose-500" : "text-brand-primary"}`}
              placeholder="Enter amount"
            />
            <div className="text-gray-500">
              ≈{" "}
              {convertedValue === " " ||
              isNaN(parseFloat(String(convertedValue)))
                ? (() => {
                    if (isBaseInput) {
                      return "0.00";
                    }
                    const asset = selectedAsset?.asset?.toLowerCase();
                    const isTwoDecimalCurrency =
                      asset === "usdc" || asset === "usdt";
                    return isTwoDecimalCurrency ? "0.00" : "0.000000";
                  })()
                : (() => {
                    if (isBaseInput) {
                      return truncateDecimals(
                        parseFloat(String(convertedValue)),
                        2
                      );
                    } else {
                      const asset = selectedAsset?.asset?.toLowerCase();
                      const isTwoDecimalCurrency =
                        asset === "usdc" || asset === "usdt";
                      const decimals = isTwoDecimalCurrency ? 2 : 8;
                      return truncateDecimals(
                        parseFloat(String(convertedValue)),
                        decimals
                      );
                    }
                  })()}{" "}
              {isBaseInput
                ? pair?.asset.toUpperCase()
                : selectedAsset?.asset.toUpperCase()}{" "}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={switchConversion}
                variant="outline"
                size="icon"
                className="rounded-full"
              >
                <ArrowUpDown className="h-4 w-4" />
              </Button>
              <Button
                onClick={maxQuoteAmount}
                variant="outline"
                className="rounded-full"
              >
                Max
              </Button>
            </div>
          </div>
          {flag && (
            <p className="mt-1 text-sm text-rose-500">
              This amount exceeds your available balance
            </p>
          )}
          {flagMinBuy && !flag && minBuy !== null && (
            <p className="mt-1 text-sm text-rose-500">
              Minimum amount must be at least{" "}
              {isBaseInput
                ? `${minBuy} ${selectedAsset?.asset?.toUpperCase()}`
                : `R${minBuy.toFixed(2)}`}
            </p>
          )}
        </div>
      </div>
      {pair && (
        <div className="flex justify-center gap-5 bg-gray-50 py-3 rounded-full text-center">
          {loadingRate ? (
            <p>
              {" "}
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            </p>
          ) : (
            !tragetAmount && (
              <div className="flex justify-center">
                {selectedAsset?.asset.toUpperCase()}/{pair?.asset.toUpperCase()}{" "}
                ≈ <strong>{parseFloat(rate?.rate).toFixed(8)}</strong>
              </div>
            )
          )}
          {tragetAmount && (
            <div className="flex justify-between items-center">
              <div>
                Buy at custom rate ETH ≈ <strong>{tragetAmount}</strong>
              </div>
            </div>
          )}
          <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
            {pair && !awaitingRate && !tragetAmount && !isPostTrade && (
              <div className="flex justify-center"></div>
            )}
            {tragetAmount && (
              <div className="flex justify-between gap-2">
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => setOrderOpen(false)}
                    className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                  >
                    <Pencil size={18} className="text-gray-700" />
                  </Button>
                </DialogTrigger>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setTragetAmount(null);
                    onTargetCancel?.();
                  }}
                  className="w-10 h-10 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                >
                  <Trash2 size={18} className="text-gray-700" />
                </Button>
              </div>
            )}
            <DialogContent className="max-w-sm rounded-2xl p-6">
              <Button
                variant="ghost"
                onClick={() => setOrderOpen(false)}
                className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
              >
                <X size={18} className="text-gray-700" />
              </Button>

              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-gray-800">
                  Create a buy order
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
                <div className="p-5 rounded-2xl bg-gray-50 flex-1 flex justify-center items-center">
                  <div className="text-sm  font-thin text-gray-600">
                    {selectedAsset?.asset.toUpperCase()}/
                    {pair?.asset.toUpperCase()} ≈ {rate?.rate}
                  </div>
                </div>

                <div className="border rounded-lg p-6">
                  <div className="text-md text-gray-700 font-semibold">
                    Buy when the price of 1 {selectedAsset?.asset.toUpperCase()}
                  </div>
                  <div className="relative flex items-center rounded-2xl mt-3">
                    <span className="absolute left-4 text-md font-semibold text-brand-primary">
                      R
                    </span>
                    <Input
                      className="flex-1 text-md font-semibold pl-10 pr-3 py-7 text-brand-primary border rounded-lg"
                      value={targetAmountHolder}
                      onChange={onTargetChange}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-center gap-4">
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    className="rounded-full py-3 px-[55px] border-gray-300 text-gray-500"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleTargetDone}
                    disabled={highTarget}
                    variant="outline"
                    className="py-3 px-[61px] text-white rounded-full bg-gradient-to-tr from-purple-500 to-rose-500 hover:opacity-90"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </>
  );
}
